import * as React from 'react';

import { ContentSection, LocalizedDate, TextField } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';

import {
  $scannerForm,
  ScannerFormState,
} from '../../../_state/scannerForm.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { ScannerDetailRecord } from '../../../types/sidecar';

type ScannerFormFieldsProps = {
  scanner?: ScannerDetailRecord | null;
  scannerId?: string;
  localScannerInfo?: {
    manufacturer: string;
    model: string;
    firmwareVersion: string;
  } | null;
  className?: string;
};

const scannerFormSelector = (state: ScannerFormState) => ({
  name: state.name,
});

export const ScannerFormFields = ({
  scanner,
  scannerId,
  localScannerInfo,
  className,
}: ScannerFormFieldsProps) => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();

  const selectedFormAtom = React.useMemo(
    () => selectAtom($scannerForm, scannerFormSelector, isEqual),
    [],
  );

  const form = useAtomValue(selectedFormAtom);
  const updateForm = useSetAtom($scannerForm);

  const handleFieldChange = React.useCallback(
    <K extends keyof ScannerFormState>(field: K) =>
      (value: ScannerFormState[K]) => {
        updateForm((prev) => ({
          ...prev,
          [field]: value,
        }));
      },
    [updateForm],
  );

  const statusLabel =
    t.hardwareView.scannersPanel.statusLabels[
      scanner?.status as keyof typeof t.hardwareView.scannersPanel.statusLabels
    ] ?? scanner?.status;

  return (
    <div className={cn('@ScannerFormFields', className)}>
      <ContentSection
        title={t.hardwareView.scannerDetailView.sections.configuration}
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <TextField
            label={t.hardwareView.scannerDetailView.fields.name}
            value={form.name}
            onChange={(value) => handleFieldChange('name')(value)}
            isRequired
          />
        </div>
      </ContentSection>

      <ContentSection
        title={t.hardwareView.scannerDetailView.sections.deviceInfo}
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-2">
          {scannerId && (
            <div className="flex flex-col gap-1">
              <span className="text-sm text-neutral/60">
                {t.hardwareView.scannerDetailView.fields.scannerId}
              </span>
              <span className="font-mono text-sm">{scannerId}</span>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.scannerDetailView.fields.uniqueId}
            </span>
            <span className="font-mono text-sm">
              {scanner?.uniqueId ?? '\u2013'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.scannerDetailView.fields.driverType}
            </span>
            <span className="text-sm font-medium">
              {scanner?.driverType ?? '\u2013'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.scannerDetailView.fields.manufacturer}
            </span>
            <span className="text-sm font-medium">
              {localScannerInfo?.manufacturer ||
                scanner?.manufacturer ||
                '\u2013'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.scannerDetailView.fields.model}
            </span>
            <span className="text-sm font-medium">
              {localScannerInfo?.model || scanner?.model || '\u2013'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.scannerDetailView.fields.firmwareVersion}
            </span>
            <span className="text-sm font-medium">
              {localScannerInfo?.firmwareVersion ||
                scanner?.firmwareVersion ||
                '\u2013'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.scannerDetailView.fields.status}
            </span>
            <span className="text-sm font-medium">
              {statusLabel ?? '\u2013'}
            </span>
          </div>
        </div>
      </ContentSection>

      <ContentSection
        title={t.hardwareView.scannerDetailView.sections.connection}
        variant="panel"
      >
        <div className="flex flex-col gap-1">
          <span className="text-sm text-neutral/60">
            {t.hardwareView.scannerDetailView.fields.sidecar}
          </span>
          {scanner?.sidecar ? (
            <button
              className="w-fit text-sm font-medium text-primary hover:underline"
              onClick={() =>
                setLocation(`/hardware/sidecars/${scanner.sidecar?.id}`)
              }
            >
              {scanner.sidecar.name}
            </button>
          ) : (
            <span className="text-sm font-medium">{'\u2013'}</span>
          )}
        </div>
      </ContentSection>

      <ContentSection
        title={t.hardwareView.scannerDetailView.sections.timestamps}
        variant="panel"
        hideDivider
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.scannerDetailView.fields.created}
            </span>
            <span className="text-sm font-medium">
              {scanner?.timestamps.createdAt ? (
                <LocalizedDate date={scanner.timestamps.createdAt} />
              ) : (
                '-'
              )}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.scannerDetailView.fields.updated}
            </span>
            <span className="text-sm font-medium">
              {scanner?.timestamps.updatedAt ? (
                <LocalizedDate date={scanner.timestamps.updatedAt} />
              ) : (
                '-'
              )}
            </span>
          </div>
        </div>
      </ContentSection>
    </div>
  );
};
